import { useCallback, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import type { BillingPeriod, PaidTier } from '../config/tiers';

// Re-exported so existing importers keep working; the type now lives in
// config/tiers alongside the rest of the checkout vocabulary.
export type { BillingPeriod, PaidTier };

/**
 * Starts a plan change for the logged-in user.
 *
 * TWO OUTCOMES, because the server does two different things. Someone with no
 * subscription gets a Stripe Checkout session and is redirected away. Someone who
 * already pays gets their existing subscription's price CHANGED IN PLACE, with
 * proration, and stays on the page — checking out again would create a second
 * subscription and bill them twice.
 *
 * So this resolves to a result rather than a boolean: the caller has to know
 * whether the browser is navigating away, whether access just changed under it,
 * or whether an invoice still needs paying.
 *
 * Never throws into the UI — failures surface via `error` and as a 'failed'
 * result, leaving the app usable.
 */

/** What happened. See the note above on why this is not a boolean. */
export type UpgradeResult =
  /** A Checkout session opened; the browser is navigating away. */
  | { kind: 'redirected' }
  /**
   * An existing subscription was changed in place. Access has changed already;
   * the price difference lands on the next invoice. No payment is attempted at
   * this point, so there is no pending-payment outcome to represent.
   */
  | { kind: 'upgraded'; tier: PaidTier }
  /** Nothing happened; `error` explains why. */
  | { kind: 'failed' };
export function useUpgrade() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startUpgrade = useCallback(async (
    tier: PaidTier,
    period: BillingPeriod = 'monthly'
  ): Promise<UpgradeResult> => {
    setError(null);
    setLoading(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) {
        setError('Please sign in again to upgrade.');
        setLoading(false);
        return { kind: 'failed' };
      }

      const res = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        // The server validates both against its own allow-list; the client never
        // sends a raw price id, so a caller cannot pick an arbitrary Stripe price.
        body: JSON.stringify({ tier, period }),
      });

      if (!res.ok) {
        let message = 'Could not start checkout. Please try again.';
        try {
          const body = await res.json();
          if (body && typeof body.error === 'string') message = body.error;
        } catch {
          /* non-JSON error body — keep the default message */
        }
        setError(message);
        setLoading(false);
        return { kind: 'failed' };
      }

      const data = await res.json();

      // New subscription: hand off to Stripe Checkout. Keep `loading` true
      // through the navigation so the button cannot be pressed twice.
      if (data && typeof data.url === 'string' && data.url) {
        window.location.href = data.url;
        return { kind: 'redirected' };
      }

      // Existing subscription changed in place.
      if (data && data.upgraded) {
        setLoading(false);
        return { kind: 'upgraded', tier };
      }

      setError('Could not start checkout. Please try again.');
      setLoading(false);
      return { kind: 'failed' };
    } catch {
      setError('Could not start checkout. Please check your connection and try again.');
      setLoading(false);
      return { kind: 'failed' };
    }
  }, []);

  return { startUpgrade, loading, error };
}
